import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Flex,
  Input,
  Link,
  Spinner,
  Tabs,
  TabPanels,
  TabPanel,
  Text,
  Textarea,
} from '@chakra-ui/core';

import { SecondaryTab, SecondaryTabList } from '../../../../common/components/Tabs';
import { apiV1 } from '../../../../common/api/api';
import {
  getUploadError,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  UPLOAD_SIZE_ERROR,
} from '../../../../common/helpers/fileUpload';
import { companyDocumentFileUrl } from '../../../../common/helpers/documentFiles';

const STATE_LABELS = {
  missing: 'Non déposée',
  under_review: 'À valider',
  valid: 'Validée',
  incorrect: 'Refusée',
};

const STATE_COLORS = {
  missing: 'semiDarkGray.600',
  under_review: 'orange.900',
  valid: 'green.700',
  incorrect: 'coral.700',
};

const isPhotoField = field => field.category === 'site_photos';

const WorksDocumentField = ({
  field,
  canWrite,
  canReview,
  busy,
  status,
  error,
  onUpload,
  onReview,
  onRemove,
}) => {
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState('');
  const inputId = `works-document-${field.companiesQuoteGroupId}-${field.key}`;
  const stateLabel =
    field.state === 'missing' && !field.required ? 'Facultative' : STATE_LABELS[field.state];

  return (
    <Box
      data-works-document={field.key}
      mb={3}
      p={3}
      bg="white"
      border="1px solid"
      borderColor="lightGray3"
      borderLeft="4px solid"
      borderLeftColor={STATE_COLORS[field.state]}
      rounded="md"
    >
      <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={2}>
        <Text fontWeight="semibold">{field.label}</Text>
        <Text fontSize="sm" fontWeight="semibold" color={STATE_COLORS[field.state]}>
          {stateLabel}
        </Text>
      </Flex>

      {field.state === 'incorrect' && field.reviewComment && (
        <Text mt={1} fontSize="sm" color="coral.700">
          Motif du refus : {field.reviewComment}
        </Text>
      )}

      {field.files.length > 0 && (
        <Box as="ul" pl={5} mt={2}>
          {field.files.map(file => (
            <Text as="li" key={file.id} fontSize="sm">
              {companyDocumentFileUrl(field.companiesQuoteGroupId, file) ? (
                <Link
                  href={companyDocumentFileUrl(field.companiesQuoteGroupId, file)}
                  isExternal
                  color="coral.900"
                >
                  {file.originalFilename}
                </Link>
              ) : (
                file.originalFilename
              )}
              {canWrite && !field.locked && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  ml={3}
                  color="coral.700"
                  isDisabled={busy}
                  aria-label={`Supprimer ${file.originalFilename}`}
                  onClick={() => onRemove(field, file)}
                >
                  Supprimer
                </Button>
              )}
            </Text>
          ))}
        </Box>
      )}

      {field.locked && (
        <Text mt={2} fontSize="sm" color="semiDarkGray.600">
          Pièce validée : dévalidez-la pour déposer ou supprimer un fichier.
        </Text>
      )}

      <Flex mt={3} flexWrap="wrap" gridGap={2} alignItems="center">
        {canWrite && !field.locked && (
          <>
            <Input
              id={inputId}
              type="file"
              accept={isPhotoField(field) ? 'image/jpeg,image/png,image/webp' : 'application/pdf'}
              multiple
              display="none"
              isDisabled={busy}
              onChange={event => {
                const input = event.target;
                const files = Array.from(input.files || []);
                if (files.length) onUpload(field, files);
                input.value = '';
              }}
            />
            <Button
              as="label"
              htmlFor={inputId}
              size="sm"
              variantColor="neutral"
              bg="coral.900"
              color="white"
              _hover={{ bg: 'semiDarkGray.900' }}
              cursor="pointer"
              isDisabled={busy}
            >
              {isPhotoField(field) ? 'Déposer des photos' : 'Déposer des fichiers'}
            </Button>
            <Text fontSize="xs" color="semiDarkGray.600">
              {isPhotoField(field) ? 'JPG, PNG ou WebP' : 'PDF'}, {MAX_UPLOAD_SIZE_MB} Mo maximum
              par fichier
            </Text>
          </>
        )}
        {canReview && field.state === 'under_review' && !refusing && (
          <>
            <Button
              type="button"
              size="sm"
              bg="semiDarkGray.900"
              variantColor="neutral"
              color="white"
              _hover={{ bg: 'darkGray' }}
              isDisabled={busy}
              onClick={() => onReview(field, 'valid')}
            >
              Valider
            </Button>
            <Button
              type="button"
              size="sm"
              bg="coral.700"
              variantColor="neutral"
              color="white"
              _hover={{ bg: 'red' }}
              isDisabled={busy}
              onClick={() => setRefusing(true)}
            >
              Refuser
            </Button>
          </>
        )}
        {canReview && field.state === 'valid' && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            variantColor={null}
            borderColor="semiDarkGray.900"
            color="semiDarkGray.900"
            isDisabled={busy}
            onClick={() => onReview(field, 'devalidated')}
          >
            Dévalider
          </Button>
        )}
      </Flex>

      {refusing && (
        <Box mt={3}>
          <Text as="label" htmlFor={`${inputId}-reason`} fontSize="sm" fontWeight="semibold">
            Motif du refus
          </Text>
          <Textarea
            id={`${inputId}-reason`}
            mt={1}
            value={reason}
            onChange={event => setReason(event.target.value)}
          />
          <Flex mt={2} gridGap={2}>
            <Button
              type="button"
              size="sm"
              bg="coral.700"
              variantColor="neutral"
              color="white"
              _hover={{ bg: 'red' }}
              isDisabled={busy || !reason.trim()}
              onClick={() => {
                onReview(field, 'incorrect', reason.trim());
                setRefusing(false);
                setReason('');
              }}
            >
              Confirmer le refus
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRefusing(false)}>
              Annuler
            </Button>
          </Flex>
        </Box>
      )}

      {status && (
        <Text role="status" mt={2} fontSize="sm">
          {status}
        </Text>
      )}
      {error && (
        <Text role="alert" mt={2} fontSize="sm" color="coral.700" whiteSpace="pre-line">
          {error}
        </Text>
      )}
    </Box>
  );
};

export const BlocHTab2 = ({ folderId, onChanged }) => {
  const authentication = useSelector(state => state.authentication || {});
  const currentUser = authentication.profile?.role ? authentication.profile : authentication.user;
  const canRead = ['administrateur', 'conseiller social', 'ergothérapeute'].includes(
    currentUser?.role,
  );
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [companyIndex, setCompanyIndex] = useState(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = () =>
    apiV1.get(`/folders/${folderId}/company_documents`).then(response => {
      setData(response.data);
      setLoadError(false);
    });

  useEffect(() => {
    if (!folderId || !canRead) return;
    load().catch(() => setLoadError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, canRead]);

  if (!canRead) return null;
  if (loadError) {
    return (
      <Text role="alert" color="coral.700" px={10}>
        Les documents des entreprises sont indisponibles. Rechargez la page pour réessayer.
      </Text>
    );
  }
  if (!data) return <Spinner aria-label="Chargement des documents des entreprises" ml={10} />;
  if (!data.companies.length) {
    return (
      <Text px={10} color="semiDarkGray.600">
        Aucune entreprise n’est associée aux travaux de ce dossier.
      </Text>
    );
  }

  const fieldKey = field => `${field.companiesQuoteGroupId}-${field.key}`;
  const run = async (field, action, failureMessage) => {
    const key = fieldKey(field);
    setBusyKey(key);
    setFeedback(current => ({ ...current, [key]: {} }));
    try {
      const status = await action();
      await load();
      if (status) setFeedback(current => ({ ...current, [key]: status }));
      if (onChangedRef.current) onChangedRef.current();
    } catch (failure) {
      setFeedback(current => ({
        ...current,
        [key]: {
          error: failure?.response?.data?.errors ? getUploadError(failure) : failureMessage,
        },
      }));
    } finally {
      setBusyKey(null);
    }
  };

  const upload = (field, files) =>
    run(
      field,
      async () => {
        const failures = [];
        let uploaded = 0;
        await files.reduce(
          (previous, file) =>
            previous.then(async () => {
              if (file.size > MAX_UPLOAD_SIZE_BYTES) {
                failures.push(`${file.name} : ${UPLOAD_SIZE_ERROR}`);
                return;
              }
              const formData = new FormData();
              formData.append('company_document[category]', field.category);
              if (field.photoPhase)
                formData.append('company_document[photo_phase]', field.photoPhase);
              formData.append('company_document[file]', file, file.name);
              try {
                await apiV1.post(
                  `/companies_quote_groups/${field.companiesQuoteGroupId}/documents`,
                  formData,
                  { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                uploaded += 1;
              } catch (uploadFailure) {
                failures.push(`${file.name} : ${getUploadError(uploadFailure)}`);
              }
            }),
          Promise.resolve(),
        );
        return {
          status: `${uploaded} fichier${uploaded > 1 ? 's' : ''} ajouté${
            uploaded > 1 ? 's' : ''
          } sur ${files.length}.`,
          error: failures.length ? failures.join('\n') : null,
        };
      },
      'Le dépôt a échoué. Vous pouvez réessayer.',
    );

  const review = (field, state, reviewComment) =>
    run(
      field,
      () =>
        apiV1
          .patch(`/companies_quote_groups/${field.companiesQuoteGroupId}/documents/review`, {
            field: field.key,
            state,
            reviewComment: reviewComment || undefined,
          })
          .then(() => null),
      'La décision n’a pas pu être enregistrée. Vous pouvez réessayer.',
    );

  const remove = (field, file) =>
    run(
      field,
      () =>
        apiV1
          .delete(`/companies_quote_groups/${field.companiesQuoteGroupId}/documents/${file.id}`)
          .then(() => null),
      'Le fichier n’a pas pu être supprimé. Vous pouvez réessayer.',
    );

  return (
    <Tabs
      id="companyDocuments"
      orientation="vertical"
      index={Math.min(companyIndex, data.companies.length - 1)}
      onChange={setCompanyIndex}
    >
      <Flex alignItems="start" position="relative">
        <SecondaryTabList title="ENTREPRISES">
          {data.companies.map(company => (
            <SecondaryTab
              w="200px"
              d="block"
              key={company.companiesQuoteGroupId}
              isValid={company.complete}
            >
              {company.companyName}
            </SecondaryTab>
          ))}
        </SecondaryTabList>

        <TabPanels flexGrow="1">
          {data.companies.map(company => (
            <TabPanel key={company.companiesQuoteGroupId} px={10}>
              <Text mb={4} color="semiDarkGray.600">
                {company.complete
                  ? 'Toutes les pièces de cette entreprise sont validées.'
                  : 'Les photos avant et après chantier, le PV de réception et la facture doivent être validés. La lettre de décharge est facultative, mais doit être validée si elle est déposée.'}
              </Text>
              {company.fields.map(item => {
                const field = { ...item, companiesQuoteGroupId: company.companiesQuoteGroupId };
                const key = fieldKey(field);
                return (
                  <WorksDocumentField
                    key={key}
                    field={field}
                    canWrite={data.canWrite}
                    canReview={data.canReview}
                    busy={Boolean(busyKey)}
                    status={feedback[key]?.status}
                    error={feedback[key]?.error}
                    onUpload={upload}
                    onReview={review}
                    onRemove={remove}
                  />
                );
              })}
            </TabPanel>
          ))}
        </TabPanels>
      </Flex>
    </Tabs>
  );
};
